import { useState } from 'react';

import {
  Box,
  Grid,
  Heading,
  ScrollPanelWithoutScrollbars,
  Tab,
  TabList,
  Text,
} from '@simplywallst/ui-core';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';

import { isCurrencyISO } from '@/constants/currencies';
import { isCountryISO } from '@/constants/countries';

import { formatterPercentage } from '@/utilities/format';
import { pluralise } from '@/utilities/pluralise';
import { formatCurrencyToScaleTruncated } from '@/utilities/portfolio/formatValue';
import { getKeyMetricColor } from '../../utils/getKeyMetricColor';

import { MinimalDropdown } from '@/components/Dropdown';
import { KeyMetric } from '@/components/KeyMetric';
import { AggregatePortfolioPerformanceChart } from '@/components/PortfolioPerformaceChart';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import Tooltip from '@/components/Tooltip';
import { AggregatePortfolioChangeCurrency } from '@/components/AggregatePortfolioChangeCurrency';

import type { PerformancePeriod } from '@/components/PortfolioPerformaceChart/types';
import type { AggregatePortfolio } from '@/hooks/portfolio/useAggregatePortfolio';

const periods: PerformancePeriod[] = ['7D', '1M', '3M', 'YTD', '1Y'];

interface Props {
  aggregatePortfolio: AggregatePortfolio;
}

export const AggregatePortfolioPerformance = ({
  aggregatePortfolio,
}: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const [period, setPeriod] = useState<PerformancePeriod>('1Y');

  const holdingOrHoldings = pluralise(
    aggregatePortfolio.analysis.holdingsCount,
    'holding',
    'holdings'
  );

  return (
    <>
      <Box
        borderTopLeftRadius="x1"
        borderTopRightRadius="x1"
        style={{
          background: `linear-gradient(
            180deg,
            rgba(255, 255, 255, 0),
            rgba(255, 255, 255, 0.05)
          )`,
        }}
      >
        <Grid
          padding="x2"
          paddingTopLg="x1_5"
          paddingBottomLg="x2"
          paddingLeftLg="x3"
          paddingRightLg="x3"
          gap="8px"
        >
          <Grid
            templateColumns="max-content auto"
            justifyContent="space-between"
            alignItems="center"
          >
            <Heading as="h2" typography="smallStrong" color="white">
              Performance vs market
            </Heading>

            {aggregatePortfolio.analysis.currencyISO && (
              <AggregatePortfolioChangeCurrency
                currencyISO={
                  isCurrencyISO(aggregatePortfolio.analysis.currencyISO)
                    ? aggregatePortfolio.analysis.currencyISO
                    : 'XXX'
                }
              />
            )}
          </Grid>

          <ScrollPanelWithoutScrollbars overflow="auto">
            <Grid templateColumns="repeat(3, max-content)" gap="32px">
              <KeyMetric>
                <Text typography="xLargeStrong" data-cy-id="total-value">
                  {formatCurrencyToScaleTruncated(
                    aggregatePortfolio.analysis.value,
                    aggregatePortfolio.analysis.currencyISO || 'USD'
                  )}
                </Text>
                <div className="text-tiny text-soft">
                  <Grid templateColumns="max-content auto" gap="4px">
                    <Tooltip
                      title={
                        <Box maxWidth="260px" padding="x1">
                          <Text
                            textAlign="center"
                            typography="tiny"
                            color="spaceGray"
                          >
                            Current market value of all your portfolios
                            combined.
                          </Text>
                        </Box>
                      }
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <Box
                        style={{
                          borderBottom: `1px dotted ${({ theme }) =>
                            theme.color.brand01}`,
                          cursor: 'helper',
                        }}
                      >
                        <Text
                          as="span"
                          typography="tiny"
                          color="whiteRGBA"
                          opacity={0.7}
                        >
                          Total Value
                        </Text>
                      </Box>
                    </Tooltip>
                    • {aggregatePortfolio.analysis.holdingsCount}{' '}
                    {holdingOrHoldings}
                  </Grid>
                </div>
              </KeyMetric>
              <KeyMetric
                color={getKeyMetricColor(aggregatePortfolio.analysis.roi)}
              >
                <Text typography="xLargeStrong" data-cy-id="roi-value">
                  {formatCurrencyToScaleTruncated(
                    aggregatePortfolio.analysis.roiValue,
                    aggregatePortfolio.analysis.currencyISO || 'USD'
                  )}
                </Text>
                <div className="text-tiny text-soft">
                  <Grid templateColumns="max-content auto" gap="4px">
                    <Tooltip
                      title={
                        <Box maxWidth="260px" padding="x1">
                          <Text
                            textAlign="center"
                            typography="tiny"
                            color="spaceGray"
                          >
                            Sum of unrealised, realised, dividends, and currency
                            returns across all your portfolios (where
                            applicable).
                          </Text>
                        </Box>
                      }
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <Box
                        style={{
                          borderBottom: `1px dotted ${({ theme }) =>
                            theme.color.brand01}`,
                          cursor: 'helper',
                        }}
                      >
                        <Text
                          as="span"
                          typography="tiny"
                          color="whiteRGBA"
                          opacity={0.7}
                        >
                          Total Returns
                        </Text>
                      </Box>
                    </Tooltip>
                    <PositiveNegativeValue
                      value={aggregatePortfolio.analysis.roi}
                    >
                      {formatterPercentage(aggregatePortfolio.analysis.roi)}
                    </PositiveNegativeValue>
                  </Grid>
                </div>
              </KeyMetric>
              <KeyMetric color="darkerGray">
                <Grid
                  templateColumns="max-content max-content"
                  gap="4px"
                  alignItems="end"
                >
                  <Text typography="xLargeStrong" data-cy-id="dividend">
                    {formatCurrencyToScaleTruncated(
                      aggregatePortfolio.analysis
                        .estimatedYearlyDividendFuture1yUSD,
                      aggregatePortfolio.analysis.currencyISO || 'USD'
                    )}
                  </Text>
                  <Text typography="baseStrong" color="whiteRGBA" opacity={0.5}>
                    /yr
                  </Text>
                </Grid>
                <div className="text-tiny text-soft">
                  <Grid templateColumns="max-content auto" gap="4px">
                    <Tooltip
                      title={
                        <Box maxWidth="260px" padding="x1">
                          <Text
                            textAlign="center"
                            typography="tiny"
                            color="spaceGray"
                          >
                            Combined expected future income from dividends over
                            the next 12 months across all your portfolios.
                          </Text>
                        </Box>
                      }
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <Box
                        style={{
                          borderBottom: `1px dotted ${({ theme }) =>
                            theme.color.brand01}`,
                          cursor: 'helper',
                        }}
                      >
                        <Text
                          as="span"
                          typography="tiny"
                          color="whiteRGBA"
                          opacity={0.7}
                        >
                          Est. Dividend
                        </Text>
                      </Box>
                    </Tooltip>
                    •{' '}
                    {formatterPercentage(
                      aggregatePortfolio.analysis.estimatedDividendYield
                    )}{' '}
                    Yield
                  </Grid>
                </div>
              </KeyMetric>
            </Grid>
          </ScrollPanelWithoutScrollbars>
        </Grid>
      </Box>
      <Box
        position="relative"
        padding="x2"
        paddingTopLg="x2"
        paddingBottomLg="x2"
        paddingRightLg="x3"
        paddingLeftLg="x3"
      >
        {isMobileScreen ? (
          <Box
            position="absolute"
            top="14px"
            right="24px"
            backgroundColor="noir"
            padding="x0_5"
            borderRadius="x1"
          >
            <MinimalDropdown
              displayType="inline-block"
              name="period-dropdown"
              options={periods.map((n) => ({ label: n, option: n }))}
              onSelect={(option) => setPeriod(option as PerformancePeriod)}
              label={period}
              value={period}
              underline={false}
            />
          </Box>
        ) : (
          <Box
            position="absolute"
            right="16px"
            width="248px"
            backgroundColor="noir"
            padding="x0_5"
            borderRadius="x1"
          >
            <TabList templateColumns="repeat(5, auto)">
              {periods.map((n) => (
                <Tab
                  key={`period-tab-${n}`}
                  styleType="period"
                  selected={period === n}
                  onClick={() => setPeriod(n)}
                  value={n}
                >
                  {n}
                </Tab>
              ))}
            </TabList>
          </Box>
        )}
        <AggregatePortfolioPerformanceChart
          market={
            isCountryISO(aggregatePortfolio.analysis.marketCountryISO)
              ? aggregatePortfolio.analysis.marketCountryISO
              : 'us'
          }
          period={period}
        />
      </Box>
    </>
  );
};
